import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { getApiBaseUrl } from '../utils/config'
import {
  PageHeader,
  Button,
  FormSelect,
  FormTextarea,
  ErrorMessage,
  Card,
  CardBody
} from '../components/common'
import ManageCategories from '../components/ManageCategories'

const UploadStaticAsset = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [categories, setCategories] = useState([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedFile, setSelectedFile] = useState(null)
  const [formData, setFormData] = useState({
    category: '',
    description: ''
  })
  const [dragActive, setDragActive] = useState(false)
  const [showManageCategories, setShowManageCategories] = useState(false)

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      console.log('Loading categories from /StaticAssets/categories')
      const response = await api.get('/StaticAssets/categories')
      console.log('Categories response:', response.data)
      // The simple endpoint returns just an array of strings
      setCategories(response.data || [])
    } catch (err) {
      console.error('Failed to load categories:', err)
      console.error('Categories error response:', err.response?.data)
      console.error('Categories error status:', err.response?.status)
      // Set some default categories if API fails
      setCategories(['Images', 'Documents', 'Videos', 'Other'])
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0])
    }
  }

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload')
      return
    }

    if (selectedFile.size === 0) {
      setError('Cannot upload empty files. Please select a file with content.')
      return
    }

    try {
      setUploading(true)
      setError(null)
      setUploadProgress(0)

      // Create FormData for direct upload to backend
      const uploadFormData = new FormData()
      
      // Try different field name variations
      uploadFormData.append('File', selectedFile)  // Capital F as per swagger
      
      if (formData.category) {
        uploadFormData.append('Category', formData.category)  // Capital C
      }
      
      if (formData.description) {
        uploadFormData.append('Description', formData.description)  // Capital D
      }

      console.log('Uploading file:', selectedFile.name)
      console.log('Selected file object:', selectedFile)
      console.log('File type:', selectedFile.type)
      console.log('File size:', selectedFile.size)
      console.log('Category:', formData.category)
      console.log('Description:', formData.description)
      
      // Debug FormData entries
      console.log('=== FormData Contents ===')
      for (let pair of uploadFormData.entries()) {
        console.log(`FormData["${pair[0]}"] =`, pair[1])
      }
      console.log('========================')

      setUploadProgress(30)

      // Try using fetch to bypass any axios issues
      const token = localStorage.getItem('authToken')
      const apiUrl = `${getApiBaseUrl()}/api/admin/StaticAssets/upload`
      
      console.log('Uploading to URL:', apiUrl)
      console.log('Using token:', token ? 'Yes' : 'No')
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // DO NOT set Content-Type - let the browser set it with boundary
        },
        body: uploadFormData
      })
      
      console.log('Response status:', response.status)
      console.log('Response ok:', response.ok)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('Error response body:', errorText)
        
        // Try to parse as JSON if possible
        try {
          const errorJson = JSON.parse(errorText)
          console.error('Error as JSON:', errorJson)
          if (errorJson.errors) {
            console.error('Validation errors:', errorJson.errors)
          }
        } catch {
          // Not JSON, that's okay
        }
        
        throw new Error(errorText || `Upload failed with status ${response.status}`)
      }
      
      const responseData = await response.json()
      console.log('Upload successful:', responseData)
      setUploadProgress(100)
      
      // Navigate to the new asset detail page
      navigate(`/static-assets/${responseData.id}`)
      
    } catch (err) {
      console.error('Upload failed:', err)
      console.error('Error response:', err.response)
      console.error('Error response data:', err.response?.data)
      console.error('Error response status:', err.response?.status)
      console.error('Validation errors:', err.response?.data?.errors)
      
      let errorMessage = 'Failed to upload file'
      
      if (err.response?.data) {
        // Try to extract meaningful error message
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message
        } else if (err.response.data.title) {
          errorMessage = err.response.data.title
        } else {
          errorMessage = `Server error (${err.response.status}): ${JSON.stringify(err.response.data)}`
        }
      } else if (err.message) {
        errorMessage = err.message
      }
      
      setError(errorMessage)
      setUploadProgress(0)
    } finally {
      setUploading(false)
    }
  }

  const handleCancel = () => {
    navigate('/static-assets')
  }

  const handleCategoriesUpdated = () => {
    loadCategories()
  }

  const removeFile = () => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return '🖼️'
    if (file.type.includes('pdf')) return '📄'
    if (file.type.startsWith('video/')) return '🎥'
    if (file.type.startsWith('audio/')) return '🎵'
    return '📎'
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Upload Static Asset"
        subtitle="Upload a new file to S3 storage"
        backLink={{ label: "Back to Assets", href: "/static-assets" }}
      />

      {error && (
        <ErrorMessage 
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <Card>
        <CardBody className="space-y-6">
          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            
            {!selectedFile ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="text-4xl mb-4">📁</div>
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Drop your file here, or click to browse
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Supports images, documents, videos, and other file types
                </p>
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  variant="primary"
                >
                  Choose File
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getFileIcon(selectedFile)}</span>
                    <div>
                      <p className="font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-sm text-gray-600">
                        {formatFileSize(selectedFile.size)} • {selectedFile.type}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={removeFile}
                    className="text-red-500 hover:text-red-700"
                    title="Remove file"
                  >
                    ✕
                  </button>
                </div>
                
                {selectedFile.type.startsWith('image/') && (
                  <div className="mt-4">
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="Preview"
                      className="max-h-48 max-w-full object-contain rounded"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Progress
              </label>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-1">{uploadProgress}% complete</p>
            </div>
          )}

          {/* Category */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Category (Optional)
              </label>
              <Button
                type="button"
                onClick={() => setShowManageCategories(true)}
                variant="ghost"
                size="small"
                disabled={uploading}
              >
                Manage Categories
              </Button>
            </div>
            <FormSelect
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              disabled={uploading}
              options={[
                { value: '', label: 'Select a category' },
                ...categories.map(category => ({ value: category, label: category }))
              ]}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <FormTextarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              placeholder="Add a description for this asset..."
              disabled={uploading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              loading={uploading}
              variant="primary"
            >
              Upload Asset
            </Button>
            <Button
              onClick={handleCancel}
              disabled={uploading}
              variant="secondary"
            >
              Cancel
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Manage Categories Modal */}
      <ManageCategories
        isOpen={showManageCategories}
        onClose={() => setShowManageCategories(false)}
        onCategoriesUpdated={handleCategoriesUpdated}
      />
    </div>
  )
}

export default UploadStaticAsset